import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tag,
  Typography,
  Tabs,
  Row,
  Col,
  Statistic,
  Descriptions,
  InputNumber,
  Alert,
  Spin,
} from 'antd';
import {
  BankOutlined,
  SwapOutlined,
  ReloadOutlined,
  PlusOutlined,
  EyeOutlined,
  DollarOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TreasuryService, {
  SettlementBankAccount,
  SettlementBankAccountPayload,
} from '../services/treasury';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchAccounts,
  fetchTransfers,
  fetchSettlements,
  createAccount,
  createTransfer,
  createSettlement,
  fetchExchangeRate,
  convertCurrency,
  fetchAccountDetail,
  fetchTransferDetail,
  fetchSettlementDetail,
} from '../store/slices/treasurySlice';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const TreasuryPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [accountForm] = Form.useForm();
  const [transferForm] = Form.useForm();
  const [settlementForm] = Form.useForm();
  const [conversionForm] = Form.useForm();
  const [bankAccountForm] = Form.useForm();

  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [settlementModalVisible, setSettlementModalVisible] = useState(false);
  const [conversionModalVisible, setConversionModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailType, setDetailType] = useState<'account' | 'transfer' | 'settlement'>(
    'account'
  );

  // 结算银行账户配置
  const [bankAccount, setBankAccount] = useState<SettlementBankAccount | null>(null);
  const [bankAccountLoading, setBankAccountLoading] = useState(false);
  const [bankAccountSaving, setBankAccountSaving] = useState(false);
  const [bankAccountModalVisible, setBankAccountModalVisible] = useState(false);

  const {
    accounts,
    currentAccount,
    transfers,
    currentTransfer,
    settlements,
    currentSettlement,
    exchangeRate,
    creating,
    converting,
    error,
  } = useAppSelector((state) => state.treasury);

  const loadBankAccount = async () => {
    setBankAccountLoading(true);
    try {
      const data = await TreasuryService.getSettlementBankAccount();
      setBankAccount(data);
    } catch (err) {
      // 接口未就绪或无配置时不打断页面，视为未配置
      setBankAccount({ configured: false });
    } finally {
      setBankAccountLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchAccounts());
    dispatch(fetchTransfers({ page: 1, limit: 20 }));
    dispatch(fetchSettlements({ page: 1, limit: 20 }));
    loadBankAccount();
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleCreateAccount = async (values: any) => {
    try {
      await dispatch(createAccount(values)).unwrap();
      message.success(t('treasury.accountCreated'));
      setAccountModalVisible(false);
      accountForm.resetFields();
    } catch (err: any) {
      message.error(err || t('treasury.createAccountFailed'));
    }
  };

  const handleCreateTransfer = async (values: any) => {
    try {
      const amount = Math.round(parseFloat(values.amount) * 100);
      await dispatch(
        createTransfer({
          ...values,
          amount,
        })
      ).unwrap();
      message.success(t('treasury.transferCreated'));
      setTransferModalVisible(false);
      transferForm.resetFields();
      dispatch(fetchAccounts());
      dispatch(fetchTransfers({ page: 1, limit: 20 }));
    } catch (err: any) {
      message.error(err || t('treasury.createTransferFailed'));
    }
  };

  const handleCreateSettlement = async (values: any) => {
    try {
      const amount = Math.round(parseFloat(values.amount) * 100);
      await dispatch(
        createSettlement({
          ...values,
          amount,
        })
      ).unwrap();
      message.success(t('treasury.settlementCreated'));
      setSettlementModalVisible(false);
      settlementForm.resetFields();
      dispatch(fetchSettlements({ page: 1, limit: 20 }));
    } catch (err: any) {
      message.error(err || t('treasury.createSettlementFailed'));
    }
  };

  const openBankAccountModal = () => {
    // 预填已配置字段；账号/IBAN 不回填明文（后端只返回脱敏值），留空表示不修改
    bankAccountForm.setFieldsValue({
      account_holder_name: bankAccount?.account_holder_name || '',
      account_holder_type: bankAccount?.account_holder_type || 'private',
      country: bankAccount?.country || '',
      currency: bankAccount?.currency || '',
      bank_name: bankAccount?.bank_name || '',
      routing_number: bankAccount?.routing_number || '',
      swift_code: bankAccount?.swift_code || '',
      bank_code: bankAccount?.bank_code || '',
      iban: '',
      account_number: '',
    });
    setBankAccountModalVisible(true);
  };

  const handleSaveBankAccount = async (values: any) => {
    const iban = (values.iban || '').trim();
    const accountNumber = (values.account_number || '').trim();

    // 校验：IBAN 与账号至少填一个。
    // 不丢账号交互：已配置时若两者都留空，视为“不修改账号”，
    // 但后端 PUT 要求至少一个，故已配置且未重填时提示用户重新填写账号。
    if (!iban && !accountNumber) {
      if (bankAccount?.configured) {
        message.warning(t('treasury.ibanOrAccountWarn'));
      } else {
        message.warning(t('treasury.ibanOrAccountAtLeastOne'));
      }
      return;
    }

    const payload: SettlementBankAccountPayload = {
      account_holder_name: values.account_holder_name?.trim(),
      account_holder_type: values.account_holder_type,
      country: (values.country || '').trim().toUpperCase(),
      currency: values.currency,
      bank_name: values.bank_name?.trim() || undefined,
      routing_number: values.routing_number?.trim() || undefined,
      swift_code: values.swift_code?.trim() || undefined,
      bank_code: values.bank_code?.trim() || undefined,
      iban: iban || undefined,
      account_number: accountNumber || undefined,
    };

    setBankAccountSaving(true);
    try {
      const data = await TreasuryService.updateSettlementBankAccount(payload);
      setBankAccount(data);
      message.success(t('treasury.bankAccountSaved'));
      setBankAccountModalVisible(false);
      bankAccountForm.resetFields();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || t('treasury.saveBankAccountFailed');
      message.error(msg);
    } finally {
      setBankAccountSaving(false);
    }
  };

  const handleConvertCurrency = async (values: any) => {
    try {
      const amount = Math.round(parseFloat(values.amount) * 100);
      await dispatch(
        convertCurrency({
          from_currency: values.from_currency,
          to_currency: values.to_currency,
          amount,
        })
      ).unwrap();
      message.success(t('treasury.convertSuccess'));
      setConversionModalVisible(false);
      conversionForm.resetFields();
      dispatch(fetchAccounts());
    } catch (err: any) {
      message.error(err || t('treasury.convertFailed'));
    }
  };

  const handleFetchExchangeRate = async (from: string, to: string) => {
    if (from && to && from !== to) {
      try {
        await dispatch(fetchExchangeRate({ from, to })).unwrap();
      } catch (err: any) {
        console.error('Failed to fetch exchange rate:', err);
      }
    }
  };

  const handleViewDetail = async (
    id: string,
    type: 'account' | 'transfer' | 'settlement'
  ) => {
    try {
      if (type === 'account') {
        await dispatch(fetchAccountDetail(id)).unwrap();
      } else if (type === 'transfer') {
        await dispatch(fetchTransferDetail(id)).unwrap();
      } else if (type === 'settlement') {
        await dispatch(fetchSettlementDetail(id)).unwrap();
      }
      setDetailType(type);
      setDetailModalVisible(true);
    } catch (err: any) {
      message.error(err || t('treasury.getDetailFailed'));
    }
  };

  const accountColumns: ColumnsType<any> = [
    {
      title: t('treasury.accountId'),
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {text}
        </Text>
      ),
    },
    {
      title: t('common.currency'),
      dataIndex: 'currency',
      key: 'currency',
      render: (text: string) => <Tag>{text}</Tag>,
    },
    {
      title: t('treasury.accountType'),
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: t('treasury.totalBalance'),
      dataIndex: 'balance',
      key: 'balance',
      render: (balance: number, record: any) => (
        <Text strong>
          {record.currency} {(balance / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: t('treasury.availableBalance'),
      dataIndex: 'available_balance',
      key: 'available_balance',
      render: (balance: number, record: any) => (
        <Text type="success">
          {record.currency} {(balance / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: t('treasury.frozenBalance'),
      dataIndex: 'pending_balance',
      key: 'pending_balance',
      render: (balance: number, record: any) => (
        <Text type="warning">
          {record.currency} {(balance / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'active' ? 'success' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.id, 'account')}
        >
          {t('common.detail')}
        </Button>
      ),
    },
  ];

  // 转账/结算共用的状态文案映射
  const txnStatusMap: Record<string, { label: string; color: string }> = {
    pending: { label: t('treasury.statusPending'), color: 'default' },
    processing: { label: t('treasury.statusProcessing'), color: 'processing' },
    completed: { label: t('treasury.statusCompleted'), color: 'success' },
    failed: { label: t('treasury.statusFailed'), color: 'error' },
    cancelled: { label: t('treasury.statusCancelled'), color: 'default' },
  };

  const transferColumns: ColumnsType<any> = [
    {
      title: t('treasury.transferId'),
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {text}
        </Text>
      ),
    },
    {
      title: t('treasury.fromAccount'),
      dataIndex: 'from_account_id',
      key: 'from_account_id',
      render: (text: string) => (
        <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>
          {text.substring(0, 12)}...
        </Text>
      ),
    },
    {
      title: t('treasury.toAccount'),
      dataIndex: 'to_account_id',
      key: 'to_account_id',
      render: (text: string) => (
        <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>
          {text.substring(0, 12)}...
        </Text>
      ),
    },
    {
      title: t('common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <Text strong>
          {record.currency} {(amount / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const config = txnStatusMap[status] || { label: status, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('treasury.completedAt'),
      dataIndex: 'completed_at',
      key: 'completed_at',
      render: (text: string) =>
        text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.id, 'transfer')}
        >
          {t('common.detail')}
        </Button>
      ),
    },
  ];

  const settlementColumns: ColumnsType<any> = [
    {
      title: t('treasury.settlementId'),
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {text}
        </Text>
      ),
    },
    {
      title: t('common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <Text strong>
          {record.currency} {(amount / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const config = txnStatusMap[status] || { label: status, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: t('treasury.destinationType'),
      dataIndex: 'destination_type',
      key: 'destination_type',
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('treasury.processedAt'),
      dataIndex: 'processed_at',
      key: 'processed_at',
      render: (text: string) =>
        text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.id, 'settlement')}
        >
          {t('common.detail')}
        </Button>
      ),
    },
  ];

  const totalBalance =
    accounts.data?.reduce((sum, acc) => {
      if (acc.currency === 'USD') {
        return sum + acc.balance;
      }
      return sum;
    }, 0) || 0;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          {t('treasury.title')}
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => dispatch(fetchAccounts())}>
            {t('common.refresh')}
          </Button>
          <Button
            type="primary"
            icon={<SwapOutlined />}
            onClick={() => setConversionModalVisible(true)}
          >
            {t('treasury.currencyConversion')}
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card loading={accounts.loading}>
            <Statistic
              title={t('treasury.totalAccounts')}
              value={accounts.data?.length || 0}
              prefix={<BankOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card loading={accounts.loading}>
            <Statistic
              title={t('treasury.usdTotalBalance')}
              value={totalBalance / 100}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="USD"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('treasury.totalTransfers')}
              value={transfers.meta?.total || 0}
              prefix={<SwapOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs defaultActiveKey="accounts">
          <TabPane
            tab={t('treasury.tabAccounts')}
            key="accounts"
          >
            <Table
              columns={accountColumns}
              dataSource={accounts.data || []}
              loading={accounts.loading}
              rowKey="id"
              locale={{ emptyText: t('common.noData') }}
              pagination={false}
            />
          </TabPane>

          <TabPane
            tab={t('treasury.tabTransfers')}
            key="transfers"
          >
            <Table
              columns={transferColumns}
              dataSource={transfers.data || []}
              loading={transfers.loading}
              rowKey="id"
              locale={{ emptyText: t('common.noData') }}
              pagination={{
                total: transfers.meta?.total || 0,
                current: transfers.meta?.current_page || 1,
                pageSize: transfers.meta?.per_page || 20,
                showSizeChanger: true,
                showTotal: (total) => t('common.totalCount', { total }),
                onChange: (page, pageSize) => {
                  dispatch(fetchTransfers({ page, limit: pageSize }));
                },
              }}
            />
          </TabPane>

          <TabPane
            tab={t('treasury.tabSettlements')}
            key="settlements"
          >
            <Card
              size="small"
              style={{ marginBottom: 16 }}
              title={
                <Space>
                  <BankOutlined style={{ color: '#2f6bff' }} />
                  <span>{t('treasury.settlementBankAccount')}</span>
                </Space>
              }
              extra={
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={openBankAccountModal}
                  style={{ background: '#2f6bff', borderColor: '#2f6bff' }}
                >
                  {bankAccount?.configured ? t('treasury.modify') : t('treasury.configure')}
                </Button>
              }
            >
              <Spin spinning={bankAccountLoading}>
                {bankAccount?.configured ? (
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label={t('treasury.accountHolder')}>
                      {bankAccount.account_holder_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('treasury.holderType')}>
                      {bankAccount.account_holder_type === 'company'
                        ? t('treasury.holderCompany')
                        : bankAccount.account_holder_type === 'private'
                        ? t('treasury.holderPrivate')
                        : bankAccount.account_holder_type || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('treasury.country')}>
                      {bankAccount.country || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('common.currency')}>
                      {bankAccount.currency ? <Tag>{bankAccount.currency}</Tag> : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('treasury.bankName')}>
                      {bankAccount.bank_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('treasury.accountNumber')}>
                      {bankAccount.account_number_masked || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="IBAN">
                      {bankAccount.iban_masked || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('treasury.swiftCode')}>
                      {bankAccount.swift_code || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('treasury.routingNumber')}>
                      {bankAccount.routing_number || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('treasury.bankCode')}>
                      {bankAccount.bank_code || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('treasury.noBankAccountTitle')}
                    description={t('treasury.noBankAccountDesc')}
                  />
                )}
              </Spin>
            </Card>

            <Table
              columns={settlementColumns}
              dataSource={settlements.data || []}
              loading={settlements.loading}
              rowKey="id"
              locale={{ emptyText: t('common.noData') }}
              pagination={{
                total: settlements.meta?.total || 0,
                current: settlements.meta?.current_page || 1,
                pageSize: settlements.meta?.per_page || 20,
                showSizeChanger: true,
                showTotal: (total) => t('common.totalCount', { total }),
                onChange: (page, pageSize) => {
                  dispatch(fetchSettlements({ page, limit: pageSize }));
                },
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Create Account Modal */}
      <Modal
        title={t('treasury.createAccount')}
        open={accountModalVisible}
        onCancel={() => {
          setAccountModalVisible(false);
          accountForm.resetFields();
        }}
        footer={null}
      >
        <Form form={accountForm} layout="vertical" onFinish={handleCreateAccount}>
          <Form.Item
            label={t('common.currency')}
            name="currency"
            rules={[{ required: true, message: t('treasury.selectCurrency') }]}
          >
            <Select placeholder={t('common.selectPlaceholder')}>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
              <Select.Option value="GBP">GBP</Select.Option>
              <Select.Option value="CNY">CNY</Select.Option>
              <Select.Option value="JPY">JPY</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={t('treasury.accountType')}
            name="type"
            rules={[{ required: true, message: t('treasury.selectAccountType') }]}
          >
            <Select placeholder={t('common.selectPlaceholder')}>
              <Select.Option value="operating">{t('treasury.accountOperating')}</Select.Option>
              <Select.Option value="reserve">{t('treasury.accountReserve')}</Select.Option>
              <Select.Option value="settlement">{t('treasury.accountSettlement')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setAccountModalVisible(false);
                  accountForm.resetFields();
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                {t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Transfer Modal */}
      <Modal
        title={t('treasury.createTransfer')}
        open={transferModalVisible}
        onCancel={() => {
          setTransferModalVisible(false);
          transferForm.resetFields();
        }}
        footer={null}
      >
        <Form form={transferForm} layout="vertical" onFinish={handleCreateTransfer}>
          <Form.Item
            label={t('treasury.fromAccount')}
            name="from_account_id"
            rules={[{ required: true, message: t('treasury.selectFromAccount') }]}
          >
            <Select
              placeholder={t('common.selectPlaceholder')}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={accounts.data?.map((acc) => ({
                label: `${acc.currency} - ${acc.type} (${(acc.available_balance / 100).toFixed(2)})`,
                value: acc.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={t('treasury.toAccount')}
            name="to_account_id"
            rules={[{ required: true, message: t('treasury.selectToAccount') }]}
          >
            <Select
              placeholder={t('common.selectPlaceholder')}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={accounts.data?.map((acc) => ({
                label: `${acc.currency} - ${acc.type}`,
                value: acc.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={t('common.amount')}
            name="amount"
            rules={[{ required: true, message: t('treasury.enterAmount') }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t('treasury.enterAmount')}
              min={0}
              step={0.01}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label={t('common.currency')}
            name="currency"
            rules={[{ required: true, message: t('treasury.selectCurrency') }]}
          >
            <Select placeholder={t('common.selectPlaceholder')}>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
              <Select.Option value="GBP">GBP</Select.Option>
              <Select.Option value="CNY">CNY</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label={t('common.description')} name="description">
            <Input.TextArea rows={3} placeholder={t('common.optional')} />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setTransferModalVisible(false);
                  transferForm.resetFields();
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                {t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Settlement Modal */}
      <Modal
        title={t('treasury.createSettlement')}
        open={settlementModalVisible}
        onCancel={() => {
          setSettlementModalVisible(false);
          settlementForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={settlementForm} layout="vertical" onFinish={handleCreateSettlement}>
          <Form.Item
            label={t('common.amount')}
            name="amount"
            rules={[{ required: true, message: t('treasury.enterAmount') }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t('treasury.enterAmount')}
              min={0}
              step={0.01}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label={t('common.currency')}
            name="currency"
            rules={[{ required: true, message: t('treasury.selectCurrency') }]}
          >
            <Select placeholder={t('common.selectPlaceholder')}>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
              <Select.Option value="GBP">GBP</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={t('treasury.destinationType')}
            name="destination_type"
            rules={[{ required: true, message: t('treasury.selectDestType') }]}
          >
            <Select placeholder={t('common.selectPlaceholder')}>
              <Select.Option value="bank_account">{t('treasury.destBankAccount')}</Select.Option>
              <Select.Option value="card">{t('treasury.destCard')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label={t('treasury.destId')} name="destination_id">
            <Input placeholder={t('common.optional')} />
          </Form.Item>

          <Form.Item
            label={t('treasury.destDetails')}
            name="destination_details"
            rules={[{ required: true, message: t('treasury.destDetailsRequired') }]}
          >
            <Input.TextArea
              rows={4}
              placeholder='{"account_number": "123456", "bank_name": "ABC Bank"}'
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setSettlementModalVisible(false);
                  settlementForm.resetFields();
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                {t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Settlement Bank Account Modal */}
      <Modal
        title={t('treasury.settlementBankAccount')}
        open={bankAccountModalVisible}
        onCancel={() => {
          setBankAccountModalVisible(false);
          bankAccountForm.resetFields();
        }}
        footer={null}
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('treasury.bankAccountInfoNote')}
        />
        <Form
          form={bankAccountForm}
          layout="vertical"
          onFinish={handleSaveBankAccount}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('treasury.accountHolderName')}
                name="account_holder_name"
                rules={[{ required: true, message: t('treasury.accountHolderNameRequired') }]}
              >
                <Input placeholder={t('treasury.accountHolderNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('treasury.holderType')}
                name="account_holder_type"
                rules={[{ required: true, message: t('treasury.holderTypeRequired') }]}
              >
                <Select placeholder={t('common.selectPlaceholder')}>
                  <Select.Option value="private">{t('treasury.holderPrivate')}</Select.Option>
                  <Select.Option value="company">{t('treasury.holderCompany')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('treasury.countryIso')}
                name="country"
                rules={[
                  { required: true, message: t('treasury.countryRequired') },
                ]}
              >
                <Select
                  placeholder={t('common.selectPlaceholder')}
                  showSearch
                  optionFilterProp="children"
                >
                  <Select.Option value="US">{t('treasury.countryUS')}</Select.Option>
                  <Select.Option value="GB">{t('treasury.countryGB')}</Select.Option>
                  <Select.Option value="DE">{t('treasury.countryDE')}</Select.Option>
                  <Select.Option value="FR">{t('treasury.countryFR')}</Select.Option>
                  <Select.Option value="CN">{t('treasury.countryCN')}</Select.Option>
                  <Select.Option value="HK">{t('treasury.countryHK')}</Select.Option>
                  <Select.Option value="SG">{t('treasury.countrySG')}</Select.Option>
                  <Select.Option value="JP">{t('treasury.countryJP')}</Select.Option>
                  <Select.Option value="AU">{t('treasury.countryAU')}</Select.Option>
                  <Select.Option value="CA">{t('treasury.countryCA')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('common.currency')}
                name="currency"
                rules={[{ required: true, message: t('treasury.selectCurrency') }]}
              >
                <Select placeholder={t('common.selectPlaceholder')}>
                  <Select.Option value="USD">USD</Select.Option>
                  <Select.Option value="EUR">EUR</Select.Option>
                  <Select.Option value="GBP">GBP</Select.Option>
                  <Select.Option value="CNY">CNY</Select.Option>
                  <Select.Option value="HKD">HKD</Select.Option>
                  <Select.Option value="SGD">SGD</Select.Option>
                  <Select.Option value="JPY">JPY</Select.Option>
                  <Select.Option value="AUD">AUD</Select.Option>
                  <Select.Option value="CAD">CAD</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t('treasury.bankName')} name="bank_name">
            <Input placeholder={t('common.optional')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="IBAN" name="iban">
                <Input
                  placeholder={
                    bankAccount?.configured && bankAccount?.iban_masked
                      ? t('treasury.leaveEmptyNoChange', { masked: bankAccount.iban_masked })
                      : t('treasury.ibanOrAccountLeaveEmpty')
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('treasury.accountNumber')} name="account_number">
                <Input
                  placeholder={
                    bankAccount?.configured && bankAccount?.account_number_masked
                      ? t('treasury.leaveEmptyNoChange', { masked: bankAccount.account_number_masked })
                      : t('treasury.ibanOrAccountLeaveEmpty')
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label={t('treasury.routingNumber')} name="routing_number">
                <Input placeholder={t('common.optional')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('treasury.swiftCode')} name="swift_code">
                <Input placeholder={t('common.optional')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('treasury.bankCode')} name="bank_code">
                <Input placeholder={t('common.optional')} />
              </Form.Item>
            </Col>
          </Row>

          {bankAccount?.configured && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('treasury.bankAccountSecurityNote')}
            />
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setBankAccountModalVisible(false);
                  bankAccountForm.resetFields();
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={bankAccountSaving}
                style={{ background: '#2f6bff', borderColor: '#2f6bff' }}
              >
                {t('common.save')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Currency Conversion Modal */}
      <Modal
        title={t('treasury.currencyConversion')}
        open={conversionModalVisible}
        onCancel={() => {
          setConversionModalVisible(false);
          conversionForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={conversionForm}
          layout="vertical"
          onFinish={handleConvertCurrency}
          onValuesChange={(changedValues, allValues) => {
            if (
              (changedValues.from_currency || changedValues.to_currency) &&
              allValues.from_currency &&
              allValues.to_currency
            ) {
              handleFetchExchangeRate(allValues.from_currency, allValues.to_currency);
            }
          }}
        >
          <Form.Item
            label={t('treasury.fromCurrency')}
            name="from_currency"
            rules={[{ required: true, message: t('treasury.selectFromCurrency') }]}
          >
            <Select placeholder={t('common.selectPlaceholder')}>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
              <Select.Option value="GBP">GBP</Select.Option>
              <Select.Option value="CNY">CNY</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={t('treasury.toCurrency')}
            name="to_currency"
            rules={[{ required: true, message: t('treasury.selectToCurrency') }]}
          >
            <Select placeholder={t('common.selectPlaceholder')}>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
              <Select.Option value="GBP">GBP</Select.Option>
              <Select.Option value="CNY">CNY</Select.Option>
            </Select>
          </Form.Item>

          {exchangeRate && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f0f2f5' }}>
              <Text>
                {t('treasury.currentRate', {
                  from: exchangeRate.from_currency,
                  rate: exchangeRate.rate.toFixed(4),
                  to: exchangeRate.to_currency,
                })}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('treasury.rateUpdatedAt', { time: dayjs(exchangeRate.updated_at).format('YYYY-MM-DD HH:mm:ss') })}
              </Text>
            </div>
          )}

          <Form.Item
            label={t('common.amount')}
            name="amount"
            rules={[{ required: true, message: t('treasury.enterAmount') }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t('treasury.enterAmount')}
              min={0}
              step={0.01}
              precision={2}
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setConversionModalVisible(false);
                  conversionForm.resetFields();
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={converting}>
                {t('treasury.convert')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={
          detailType === 'account'
            ? t('treasury.accountDetail')
            : detailType === 'transfer'
            ? t('treasury.transferDetail')
            : t('treasury.settlementDetail')
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            {t('common.close')}
          </Button>,
        ]}
        width={800}
      >
        {detailType === 'account' && currentAccount && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('treasury.accountId')} span={2}>
              <Text copyable>{currentAccount.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.currency')}>
              <Tag>{currentAccount.currency}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('treasury.type')}>{currentAccount.type}</Descriptions.Item>
            <Descriptions.Item label={t('treasury.totalBalance')}>
              <Text strong>
                {currentAccount.currency} {(currentAccount.balance / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('treasury.availableBalance')}>
              <Text type="success">
                {currentAccount.currency}{' '}
                {(currentAccount.available_balance / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('treasury.frozenBalance')} span={2}>
              <Text type="warning">
                {currentAccount.currency}{' '}
                {(currentAccount.pending_balance / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.status')}>
              <Tag color={currentAccount.status === 'active' ? 'success' : 'default'}>
                {currentAccount.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.createdAt')}>
              {dayjs(currentAccount.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}

        {detailType === 'transfer' && currentTransfer && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('treasury.transferId')} span={2}>
              <Text copyable>{currentTransfer.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('treasury.fromAccount')} span={2}>
              <Text copyable>{currentTransfer.from_account_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('treasury.toAccount')} span={2}>
              <Text copyable>{currentTransfer.to_account_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.amount')}>
              <Text strong>
                {currentTransfer.currency} {(currentTransfer.amount / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.status')}>
              <Tag
                color={
                  currentTransfer.status === 'completed'
                    ? 'success'
                    : currentTransfer.status === 'failed'
                    ? 'error'
                    : 'processing'
                }
              >
                {currentTransfer.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.description')} span={2}>
              {currentTransfer.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.createdAt')}>
              {dayjs(currentTransfer.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label={t('treasury.completedAt')}>
              {currentTransfer.completed_at
                ? dayjs(currentTransfer.completed_at).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}

        {detailType === 'settlement' && currentSettlement && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('treasury.settlementId')} span={2}>
              <Text copyable>{currentSettlement.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.amount')}>
              <Text strong>
                {currentSettlement.currency} {(currentSettlement.amount / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.status')}>
              <Tag
                color={
                  currentSettlement.status === 'completed'
                    ? 'success'
                    : currentSettlement.status === 'failed'
                    ? 'error'
                    : 'processing'
                }
              >
                {currentSettlement.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('treasury.destinationType')} span={2}>
              {currentSettlement.destination_type}
            </Descriptions.Item>
            <Descriptions.Item label={t('treasury.destDetails')} span={2}>
              <pre style={{ margin: 0 }}>
                {JSON.stringify(currentSettlement.destination_details, null, 2)}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.createdAt')}>
              {dayjs(currentSettlement.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label={t('treasury.processedAt')}>
              {currentSettlement.processed_at
                ? dayjs(currentSettlement.processed_at).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default TreasuryPage;
